import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BulkAssignModal = ({ isOpen, onClose, catalogProducts = [], existingProductIds = [], onBulkAssign }) => {
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [assigning, setAssigning] = useState(false);

  const available = useMemo(() =>
    catalogProducts?.filter(p =>
      !existingProductIds?.includes(p?.id) &&
      (p?.productName?.toLowerCase()?.includes(search?.toLowerCase()) ||
       p?.oemBrand?.toLowerCase()?.includes(search?.toLowerCase()) ||
       p?.category?.toLowerCase()?.includes(search?.toLowerCase()))
    ),
    [catalogProducts, existingProductIds, search]
  );

  const toggleSelect = (id) => {
    setSelected(prev =>
      prev?.includes(id) ? prev?.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (selected?.length === available?.length) {
      setSelected([]);
    } else {
      setSelected(available?.map(p => p?.id));
    }
  };

  const handleAssign = async () => {
    if (selected?.length === 0) return;
    setAssigning(true);
    try {
      const selectedProducts = catalogProducts?.filter(p => selected?.includes(p?.id));
      await onBulkAssign(selectedProducts);
      setSelected([]);
      onClose();
    } finally {
      setAssigning(false);
    }
  };

  if (!isOpen) return null;

  const CATEGORY_COLORS = {
    networking: 'bg-blue-100 text-blue-700',
    security: 'bg-red-100 text-red-700',
    cloud: 'bg-purple-100 text-purple-700',
    software: 'bg-green-100 text-green-700',
    telecom: 'bg-orange-100 text-orange-700',
    iot: 'bg-cyan-100 text-cyan-700',
    hardware: 'bg-slate-100 text-slate-700',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[700px] max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-24 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Bulk Assign from Catalog</h2>
            <p className="text-sm text-slate-500 mt-2">Select multiple products to assign to this partner at once</p>
          </div>
          <button onClick={onClose} className="p-8 hover:bg-slate-100 rounded-lg transition-colors">
            <Icon name="X" size={20} className="text-slate-500" />
          </button>
        </div>

        {/* Search + Select All */}
        <div className="p-16 border-b border-slate-100 space-y-12">
          <div className="relative">
            <Icon name="Search" size={16} className="absolute left-12 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, brand, or category..."
              value={search}
              onChange={(e) => setSearch(e?.target?.value)}
              className="w-full pl-36 pr-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div className="flex items-center justify-between">
            <button
              onClick={toggleAll}
              className="flex items-center gap-8 text-sm text-primary font-medium hover:underline"
            >
              <div className={`w-16 h-16 rounded border-2 flex items-center justify-center transition-colors ${selected?.length === available?.length && available?.length > 0 ? 'bg-primary border-primary' : 'border-slate-300'}`}>
                {selected?.length === available?.length && available?.length > 0 && (
                  <Icon name="Check" size={10} className="text-white" />
                )}
              </div>
              {selected?.length === available?.length && available?.length > 0 ? 'Deselect All' : 'Select All'}
            </button>
            <span className="text-xs text-slate-500">
              {available?.length} available · {selected?.length} selected
            </span>
          </div>
        </div>

        {/* Product List */}
        <div className="flex-1 overflow-y-auto p-16 space-y-8">
          {available?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-48 text-center">
              <div className="p-16 bg-slate-100 rounded-full mb-16">
                <Icon name="Package" size={28} className="text-slate-400" />
              </div>
              <p className="text-slate-600 font-medium">No products available</p>
              <p className="text-slate-400 text-sm mt-4">
                {existingProductIds?.length > 0 ? 'All catalog products are already assigned' : 'No products in the catalog yet'}
              </p>
            </div>
          ) : (
            available?.map(product => {
              const isSelected = selected?.includes(product?.id);
              return (
                <button
                  key={product?.id}
                  onClick={() => toggleSelect(product?.id)}
                  className={`w-full flex items-center gap-12 p-14 rounded-xl border-2 transition-all text-left ${isSelected ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                >
                  <div className={`w-20 h-20 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-primary border-primary' : 'border-slate-300'}`}>
                    {isSelected && <Icon name="Check" size={12} className="text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-8 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900">{product?.productName}</span>
                      {product?.category && (
                        <span className={`px-8 py-2 rounded-full text-xs font-medium ${CATEGORY_COLORS?.[product?.category] || 'bg-slate-100 text-slate-600'}`}>
                          {product?.category}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-12 mt-4 text-xs text-slate-500">
                      {product?.oemBrand && <span>{product?.oemBrand}</span>}
                      {product?.modelSku && <span>SKU: {product?.modelSku}</span>}
                      {product?.marginCategory && <span className="capitalize">Margin: {product?.marginCategory}</span>}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-20 border-t border-slate-200 bg-slate-50">
          <span className="text-sm text-slate-600">
            {selected?.length > 0 ? `${selected?.length} product${selected?.length > 1 ? 's' : ''} selected` : 'No products selected'}
          </span>
          <div className="flex items-center gap-12">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              variant="default"
              onClick={handleAssign}
              disabled={selected?.length === 0 || assigning}
              iconName={assigning ? undefined : 'Zap'}
              iconPosition="left"
              iconSize={16}
            >
              {assigning ? 'Assigning...' : `Assign ${selected?.length > 0 ? selected?.length : ''} Product${selected?.length !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkAssignModal;
